import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaEye,
  FaCheckCircle,
  FaArrowLeft,
  FaUserCheck,
  FaWallet,
  FaClock,
  FaCrown,
  FaChartLine,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa";
import { getVendorAllPlans } from "../../redux/services/SubscriptionServices/Subscription.Services";
import Pagination from "../../common/Pagination";
import { errorToast } from "../../Utils/toast";
import { isSplitLeadVendor } from "../../Utils/role";
import SharedPlanBalance from "./SharedPlanBalance";
function SubscriptionHistory() {
  const navigate = useNavigate();
  const [tableLoading, setTableLoading] = useState(false);
  const [subscription, setSubscription] = useState([]);
  const [vendorInfo, setVendorInfo] = useState(null); 
  const [totalAmountPaid, setTotalAmountPaid] = useState(0);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const fetchSubscriptionList = async (pageNo = 1) => {
    try {
      setTableLoading(true);
      const payload = { page: pageNo, per_page: limit };
      const res = await getVendorAllPlans(payload);    
      const list = Array.isArray(res?.subscriptions)
        ? res.subscriptions
        : Array.isArray(res?.plans)
        ? res.plans
        : Array.isArray(res?.data)
        ? res.data
        : [];     
      setSubscription(list);
      if (res?.vendor || res?.user) {
        setVendorInfo(res.vendor || res.user);
      }

      setTotalAmountPaid(res?.total_amount_paid ?? 0);
      setTotalSubscriptions(res?.total_subscriptions ?? list.length);
      setPage(res?.current_page ?? 1);
      setTotalPages(res?.last_page ?? 1);
    } catch (error) {
      console.log(error);
      errorToast("Failed to load your plans");
      setSubscription([]);
    } finally {
      setTableLoading(false);
    }
  };
  useEffect(() => {
    fetchSubscriptionList(1);
  }, [limit]);
  const totalAiUsage = useMemo(() => {
    return subscription.reduce(
      (acc, item) => {
        const ai = item?.usage?.ai_calls;
        if (!ai) return acc;
        acc.used += Number(ai.used) || 0;
        acc.limit += Number(ai.limit) || 0;
        acc.remaining += Number(ai.remaining) || 0;
        return acc;
      },
      { used: 0, limit: 0, remaining: 0 }
    );
  }, [subscription]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !tableLoading) {
      setPage(newPage);
      fetchSubscriptionList(newPage);
    }
  };

  const splitVendor = isSplitLeadVendor();

  const featureLabel = (feature) => {
    if (!splitVendor) return feature;
    if (/ai\s*calls/i.test(String(feature))) return "Talk time in seconds";
    return feature;
  };

  const formatPrice = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    return `₹${Number(val).toLocaleString("en-IN")}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getSubStatusBadge = (status) => {
    if (!status) return "status-inactive";
    const key = String(status).toLowerCase();
    const map = {
      active: "status-active",
      inactive: "status-inactive",
      cancelled: "status-cancelled",
      canceled: "status-cancelled",
      expired: "status-expired",
      pending: "status-pending",
      approved: "status-active",
    };
    return map[key] || "status-inactive";
  };

  const getPaymentBadge = (status) => {
    if (!status) return "status-inactive";
    const key = String(status).toLowerCase();
    const map = {
      paid: "status-active",
      success: "status-active",
      pending: "status-pending",
      failed: "status-cancelled",
      unpaid: "status-pending",
    };
    return map[key] || "status-inactive";
  };

  return (
    <div className="planlist-page">
      {/* TOP BAR - Matching Vendor Page */}
      <div className="planlist-topbar d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="page-title mb-1">My Subscriptions</h4>
          <div className="page-subtitle text-muted" style={{ fontSize: '13px' }}>
            <FaUserCheck size={11} /> Your plan history
          </div>
        </div>
        <button className="add-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft className="me-1" /> Back
        </button>
      </div>

      {/* DASHBOARD ROW (Vendor Card + Stats) */}
      <div className="dashboard-row d-flex gap-4 mb-4" style={{ flexWrap: 'wrap' }}>
        
        {/* USER/VENDOR CARD (Left side) - Matching Vendor Page */}
        <div className="vendor-card" style={{ flex: '1 1 350px' }}>
          <div className="vendor-card-top d-flex align-items-center gap-3 mb-3">
            <div className="vendor-avatar-large">
              {vendorInfo?.name?.charAt(0) || "U"}
            </div>
            <div className="vendor-info-main">
              <div className="vendor-name-large d-flex align-items-center gap-2">
                <h5 className="mb-0">{vendorInfo?.name || "User Name"}</h5>
                {vendorInfo?.kyc_status && (
                  <span className={`vendor-kyc ${getSubStatusBadge(vendorInfo.kyc_status)}`} style={{ fontSize: '10px', padding: '2px 8px' }}>
                    <FaUserCheck size={9} /> KYC {vendorInfo.kyc_status}
                  </span>
                )}
              </div>
              <div className="vendor-business text-muted mt-1" style={{ fontSize: '13px' }}>
                {vendorInfo?.business_name || "Business Name"}
              </div>
            </div>
          </div>

          <div className="vendor-contacts-grid d-flex gap-3 mt-4">
            <div className="vendor-contact-item flex-fill bg-dark p-3 rounded d-flex align-items-center gap-2">
              <FaEnvelope size={14} className="text-primary" />
              <div>
                <div className="vc-label text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>EMAIL</div>
                <div className="vc-value" style={{ fontSize: '13px' }}>{vendorInfo?.email || "-"}</div>
              </div>
            </div>
            <div className="vendor-contact-item flex-fill bg-dark p-3 rounded d-flex align-items-center gap-2">
              <FaPhone size={14} className="text-primary" />
              <div>
                <div className="vc-label text-muted" style={{ fontSize: '10px', textTransform: 'uppercase' }}>MOBILE</div>
                <div className="vc-value" style={{ fontSize: '13px' }}>{vendorInfo?.mobile || "-"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* STATS CARDS (Right side) - Matching Vendor Page */}
        <div className="stats-grid d-flex flex-wrap gap-3" style={{ flex: '1 1 500px' }}>
          
          {/* Amount Paid */}
          {/* <div className="stat-box bg-dark p-4 rounded" style={{ flex: '1 1 30%', minWidth: '150px' }}>
            <div className="stat-icon stat-green mb-3">
              <FaWallet size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>TOTAL AMOUNT PAID</div>
              <div className="stat-value stat-value-green" style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {formatPrice(totalAmountPaid)}
              </div>
            </div>
          </div> */}

          {/* Total Subscriptions */}
          <div className="stat-box bg-dark p-4 rounded" style={{ flex: '1 1 30%', minWidth: '150px' }}>
            <div className="stat-icon stat-blue mb-3">
              <FaCrown size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>TOTAL SUBSCRIPTIONS</div>
              <div className="stat-value" style={{ fontSize: '24px', fontWeight: 'bold' }}>{totalSubscriptions}</div>
            </div>
          </div>

          {/* Total AI Calls Usage */}
          <div className="stat-box bg-dark p-4 rounded" style={{ flex: '1 1 30%', minWidth: '150px' }}>
            <div className="stat-icon stat-purple mb-3" style={{ color: '#8b5cf6' }}>
              <FaChartLine size={20} />
            </div>
            <div className="stat-content">
              <div className="stat-label text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>TOTAL AI CALLS USAGE</div>
              <div className="stat-value" style={{ fontSize: '22px', fontWeight: 'bold' }}>
                {totalAiUsage.used.toLocaleString("en-IN")} / {totalAiUsage.limit.toLocaleString("en-IN")}
              </div>
              <div className="stat-sub text-muted mt-1" style={{ fontSize: '12px' }}>
                {totalAiUsage.remaining.toLocaleString("en-IN")} remaining
              </div>
            </div>
          </div>
          
        </div>
      </div>

      <div className="planlist-main">
        {tableLoading ? (
          <div className="loader-wrap text-center py-5">
            <div className="spinner-border text-primary"></div>
            <p className="mt-2">Loading your plans...</p>
          </div>
        ) : subscription.length > 0 ? (
          <>
            <div className="plan-list">
              {subscription.map((item) => (
                <div className="plan-card-full" key={item.id}>
                  <div className="plan-main">
                    <div className="plan-header">
                      <div className="plan-header-left">
                        <h4 className="plan-title">
                          {splitVendor && /ai\s*call/i.test(item?.plan_name || "")
                            ? "Shared talk time"
                            : item?.plan_name || "Untitled Plan"}
                        </h4>
                        <div className="plan-tags">
                          {item?.module_name && (
                            <span className="plan-tag purple">
                              {item.module_name}
                            </span>
                          )}
                          {/* {item?.category_name && (
                            <span className="plan-tag blue">
                              {splitVendor && /ai\s*call/i.test(item.category_name)
                                ? "Talk time"
                                : item.category_name}
                            </span>
                          )} */}
                        </div>
                      </div>
                      <span
                        className={`plan-status ${getSubStatusBadge(
                          item?.status
                        )}`}
                      >
                         {item?.status || "-"}
                      </span>
                    </div>

                    {/* <div className="plan-info-row">
                      <div className="plan-info-item">
                        <div className="plan-info-label">Amount Paid</div>
                        <div className="plan-info-value green">
                          {formatPrice(item?.amount_paid)}
                        </div>
                      </div>
                      <div className="plan-divider" />
                      <div className="plan-info-item">
                        <div className="plan-info-label">Payment</div>
                        <span
                          className={`plan-payment ${getPaymentBadge(
                            item?.payment_status
                          )}`}
                        >
                          {item?.payment_status || "-"}
                        </span>
                      </div>
                    </div> */}

                    <div className="plan-dates-row">
                      <div className="plan-date-item">
                        <div className="date-dot dot-green"></div>
                        <div>
                          <div className="plan-date-label">Start Date</div>
                          <div className="plan-date-value">
                            {formatDate(item?.start_date)}
                          </div>
                        </div>
                      </div>
                      <div className="plan-date-line">
                        <div className="date-line-progress" />
                      </div>
                      <div className="plan-date-item">
                        <div className="date-dot dot-red"></div>
                        <div>
                          <div className="plan-date-label">End Date</div>
                          <div className="plan-date-value">
                            {formatDate(item?.end_date)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {item?.features?.length > 0 && (
                      <div className="plan-features-row">
                        <div className="plan-features-title">
                          <FaCheckCircle size={11} /> Features Included
                        </div>
                        <div className="plan-features-grid">
                          {item.features.map((f, i) => (
                            <div key={i} className="plan-feature-chip">
                              <FaCheckCircle
                                size={11}
                                className="plan-feature-icon"
                              />
                              <span>{featureLabel(f)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ========== RIGHT: Sidebar ========== */}
                  <div className="plan-side">
                    {splitVendor ? (
                      <SharedPlanBalance />
                    ) : item?.usage?.ai_calls && (
                      <div className="usage-block">
                        <div className="usage-header-row">
                          <FaChartLine size={13} className="usage-header-icon" />
                          <span>AI Calls Usage</span>
                        </div>

                        <div className="usage-numbers">
                          <div className="usage-big">
                            {item.usage.ai_calls.used}
                            <span className="usage-split">
                              /{item.usage.ai_calls.limit}
                            </span>
                          </div>
                          <div className="usage-percent">
                            {item.usage.ai_calls.limit > 0 ? Math.round(
                              (item.usage.ai_calls.used /
                                item.usage.ai_calls.limit) *
                                100
                            ) : 0}
                            %
                          </div>
                        </div>

                        <div className="usage-bar">
                          <div
                            className="usage-fill"
                            style={{
                              width: `${
                                item.usage.ai_calls.limit > 0 ? (item.usage.ai_calls.used /
                                  item.usage.ai_calls.limit) *
                                100 : 0
                              }%`,
                            }}
                          />
                        </div>

                        <div className="usage-remaining">
                          <span className="dot-green-sm" />
                          {item.usage.ai_calls.remaining} remaining
                        </div>
                      </div>
                    )}

                    {item?.payment_method && (
                      <div className="payment-block">
                        <div className="payment-label">Payment Method</div>
                        <div className="payment-value">
                          {item.payment_method}
                        </div>
                      </div>
                    )}

                    <button
                      className="plan-btn"
                      onClick={() => navigate("/subscriptions-transactions-list")}
                    >
                      <FaEye size={13} /> View Transaction
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="d-flex justify-content-end mt-4">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <div className="empty-state text-center py-5">
            <div className="empty-icon" style={{ fontSize: '40px' }}>📋</div>
            <div className="empty-title mt-3">No subscriptions yet</div>
            <div className="empty-sub text-muted">
              Aapne abhi tak koi plan subscribe nahi kiya.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SubscriptionHistory;